import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { groupBuyApi, reviewApi } from '../api';
import { GroupBuy, Review } from '../types';
import { useAuthStore } from '../store/authStore';
import { useCartStore } from '../store/cartStore';
import { toImageUrl } from '../utils/image';
import Loading from '../components/common/Loading';
import CountdownTimer from '../components/groupbuy/CountdownTimer';

type ReviewSort = 'id,desc' | 'rating,desc' | 'rating,asc';

const GOODS_LABEL: Record<string, string> = {
  KEYRING: '🔑 Keyring', ACRYLIC: '🎨 Acrylic Stand', STICKER: '🖼 Sticker Pack',
  POSTER: '📜 Poster', BADGE: '📌 Badge', TAPESTRY: '🖼 Tapestry', OTHER: '🎁 Other',
};

const STATUS_INFO: Record<string, { text: string; color: string; desc: string }> = {
  COLLECTING: { text: 'On Sale', color: '#b39ddb', desc: 'On sale now — order before the deadline!' },
  CLOSED: { text: 'Closed', color: '#888', desc: 'Sale ended. Processing orders.' },
  PURCHASING: { text: 'Preparing', color: '#fb8c00', desc: 'We are preparing your order from the manufacturer.' },
  SHIPPING: { text: 'Shipping', color: '#1976d2', desc: 'Items are on the way!' },
  DONE: { text: 'Done', color: '#4caf50', desc: 'All orders have been delivered.' },
  FAILED: { text: 'Cancelled', color: '#e53935', desc: 'Production was cancelled — full refund issued.' },
};

const GroupBuyDetailPage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { addGroupBuyItem } = useCartStore();
  const [joining, setJoining] = useState(false);

  const [groupBuy, setGroupBuy] = useState<GroupBuy | null>(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [selectedImg, setSelectedImg] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);

  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewSort, setReviewSort] = useState<ReviewSort>('id,desc');
  const [reviewPage, setReviewPage] = useState(0);
  const [reviewTotalPages, setReviewTotalPages] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [avgRating, setAvgRating] = useState(0);

  useEffect(() => {
    if (!code) return;
    groupBuyApi.getOne(code)
      .then(r => setGroupBuy(r.data.data))
      .finally(() => setLoading(false));
  }, [code]);

  useEffect(() => {
    if (!groupBuy) return;
    reviewApi.getByProduct(groupBuy.id, reviewPage, reviewSort).then(r => {
      const d = r.data.data;
      setReviews(d.reviews?.content ?? []);
      setReviewTotalPages(d.reviews?.totalPages ?? 0);
      setReviewCount(d.totalCount ?? 0);
      setAvgRating(d.averageRating ?? 0);
    });
  }, [groupBuy, reviewPage, reviewSort]);

  if (loading || !groupBuy) return <Loading />;

  const images = [groupBuy.thumbnailUrl, ...(groupBuy.images ?? []).map(i => i.url)].filter(Boolean) as string[];
  const variants = groupBuy.variants ?? [];
  const hasVariants = variants.length > 0;
  const selectedVariant = variants.find(v => v.id === selectedVariantId) ?? null;
  const needVariant = hasVariants && !selectedVariant;
  const mainImg = selectedVariant?.imageUrl ? selectedVariant.imageUrl : (images[selectedImg] ?? '');
  const isUpcoming = groupBuy.phase === 'UPCOMING';
  const info = isUpcoming
    ? { text: 'Upcoming', color: '#9575cd', desc: groupBuy.startAt ? `Opens on ${new Date(groupBuy.startAt).toLocaleString()}` : 'Opening soon.' }
    : (STATUS_INFO[groupBuy.status] ?? { text: groupBuy.status, color: '#888', desc: '' });
  const remaining = Math.max(0, (groupBuy.stock ?? 0) - (groupBuy.currentQuantity ?? 0));
  const soldOut = remaining <= 0;
  const canJoin = groupBuy.phase === 'COLLECTING' && !soldOut;
  const maxQty = Math.max(1, Math.min(groupBuy.maxPerPerson ?? remaining, remaining));

  const handleJoin = async () => {
    if (!user) { navigate('/login'); return; }
    if (needVariant) { alert('Please select a design'); return; }
    setJoining(true);
    try {
      await addGroupBuyItem(groupBuy.id, qty, selectedVariantId);
      navigate('/cart');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to add to cart');
    } finally {
      setJoining(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.topSection}>
        <div style={styles.imageSection}>
          <img src={toImageUrl(mainImg)} alt={groupBuy.title} style={styles.mainImage} />
          {images.length > 1 && (
            <div style={styles.thumbRow}>
              {images.map((url, i) => (
                <img key={i} src={toImageUrl(url)} alt="" style={{ ...styles.thumb, ...(selectedImg === i ? styles.thumbActive : {}) }}
                  onClick={() => setSelectedImg(i)} />
              ))}
            </div>
          )}
        </div>

        <div style={styles.infoSection}>
          {groupBuy.webtoonTitle && <p style={styles.webtoon}>{groupBuy.webtoonTitle}</p>}
          {groupBuy.artist && <p style={styles.artist}>by {groupBuy.artist}</p>}
          <h1 style={styles.title}>{groupBuy.title}</h1>

          <div style={{ marginBottom: 8 }}>
            <span style={{ ...styles.statusBadge, background: info.color }}>{info.text}</span>
            <span style={styles.goodsType}>{GOODS_LABEL[groupBuy.goodsType] ?? groupBuy.goodsType}</span>
          </div>
          <p style={styles.statusDesc}>{info.desc}</p>

          {reviewCount > 0 && (
            <div style={styles.rating}>
              <span style={{ color: '#f5b400' }}>★</span> {avgRating.toFixed(1)}
              <span style={{ color: '#aaa', marginLeft: 4 }}>({reviewCount})</span>
            </div>
          )}

          <p style={styles.price}>${Number(groupBuy.priceUsd).toFixed(2)}<span style={styles.perItem}> / item</span></p>

          {groupBuy.estimatedShippingWeeks && (
            <p style={styles.shippingNote}>🚢 Estimated shipping: {groupBuy.estimatedShippingWeeks} after group buy closes</p>
          )}

          <div style={styles.divider} />

          <div style={{ marginBottom: 16 }}>
            <p style={styles.sectionLabel}>Deadline</p>
            {groupBuy.phase === 'COLLECTING'
              ? <CountdownTimer deadline={groupBuy.deadline} />
              : <p style={{ fontSize: 13, color: '#888' }}>{new Date(groupBuy.deadline).toLocaleDateString()}</p>}
          </div>

          {soldOut && (
            <div style={{ marginBottom: 20 }}>
              <p style={styles.sectionLabel}>Stock</p>
              <p style={{ fontSize: 14, fontWeight: 600, color: '#e53935' }}>Sold out</p>
            </div>
          )}

          {canJoin && (
            <>
              {hasVariants && (
                <div style={{ marginBottom: 16 }}>
                  <p style={styles.sectionLabel}>Design</p>
                  <div style={styles.variantChips}>
                    {variants.map(v => {
                      const disabled = !v.available;
                      const active = selectedVariantId === v.id;
                      return (
                        <button key={v.id} type="button" disabled={disabled}
                          onClick={() => setSelectedVariantId(v.id)}
                          style={{ ...styles.variantChip, ...(active ? styles.variantChipActive : {}), ...(disabled ? styles.variantChipDisabled : {}) }}>
                          {v.imageUrl && <img src={toImageUrl(v.imageUrl)} alt="" style={styles.variantChipImg} />}
                          <span>{v.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div style={styles.qtyRow}>
                <span style={styles.sectionLabel}>Quantity</span>
                <div style={styles.qtyControl}>
                  <button style={styles.qtyBtn} onClick={() => setQty(q => Math.max(1, q - 1))}>−</button>
                  <span style={styles.qtyValue}>{qty}</span>
                  <button style={styles.qtyBtn} onClick={() => setQty(q => Math.min(maxQty, q + 1))}>+</button>
                </div>
              </div>
              <div style={styles.totalRow}>
                <span style={styles.sectionLabel}>Total</span>
                <span style={styles.totalPrice}>${(Number(groupBuy.priceUsd) * qty).toFixed(2)}</span>
              </div>
              <button style={{ ...styles.joinBtn, ...((needVariant || joining) ? { opacity: 0.5 } : {}) }} onClick={handleJoin} disabled={needVariant || joining}>
                {needVariant ? 'Select a design' : joining ? 'Adding…' : 'Add to Cart →'}
              </button>
            </>
          )}
        </div>
      </div>

      {groupBuy.description && (
        <div style={styles.desc}>
          <h2 style={styles.sectionTitle}>About this item</h2>
          <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, color: '#555' }}>{groupBuy.description}</p>
        </div>
      )}

      <div style={styles.reviewSection}>
        <div style={styles.reviewHeader}>
          <h2 style={styles.sectionTitle}>Reviews <span style={{ color: '#b39ddb' }}>{reviewCount}</span></h2>
          <div style={styles.sortToggle}>
            {(['id,desc', 'rating,desc', 'rating,asc'] as ReviewSort[]).map(s => (
              <button key={s} style={{ ...styles.sortBtn, ...(reviewSort === s ? styles.sortBtnActive : {}) }}
                onClick={() => { setReviewSort(s); setReviewPage(0); }}>
                {s === 'id,desc' ? 'Latest' : s === 'rating,desc' ? 'Highest' : 'Lowest'}
              </button>
            ))}
          </div>
        </div>
        {reviews.length === 0
          ? <p style={styles.noReview}>No reviews yet.</p>
          : reviews.map(r => (
            <div key={r.id} style={styles.reviewItem}>
              <div style={styles.reviewMeta}>
                <span style={{ fontWeight: 600 }}>{r.userName}</span>
                <span>{'⭐'.repeat(r.rating)}</span>
                <span style={{ color: '#bbb', fontSize: 12 }}>{r.createdAt.slice(0, 10)}</span>
              </div>
              <p style={{ fontSize: 14, color: '#555', lineHeight: 1.6 }}>{r.content}</p>
            </div>
          ))}
        {reviewTotalPages > 1 && (
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 16 }}>
            {Array.from({ length: reviewTotalPages }, (_, i) => (
              <button key={i} style={{ width: 32, height: 32, border: '1px solid #e8e8e8', background: reviewPage === i ? '#b39ddb' : '#fff', color: reviewPage === i ? '#fff' : '#555', borderRadius: 6, cursor: 'pointer' }}
                onClick={() => setReviewPage(i)}>{i + 1}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1100, margin: '40px auto', padding: '0 20px' },
  topSection: { display: 'flex', gap: 48, marginBottom: 60, flexWrap: 'wrap' },
  imageSection: { flex: '0 0 460px', maxWidth: '100%' },
  mainImage: { width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 12, background: '#f5f5f5' },
  thumbRow: { display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' },
  thumb: { width: 60, height: 60, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', border: '2px solid #eee' },
  thumbActive: { borderColor: '#b39ddb' },
  infoSection: { flex: 1, minWidth: 280 },
  webtoon: { fontSize: 12, color: '#b39ddb', fontWeight: 700, marginBottom: 2 },
  artist: { fontSize: 12, color: '#999', marginBottom: 8 },
  title: { fontSize: 24, fontWeight: 800, color: '#333', marginBottom: 12, lineHeight: 1.3 },
  statusBadge: { color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12, marginRight: 8 },
  goodsType: { fontSize: 12, color: '#888', background: '#f5f5f5', padding: '3px 10px', borderRadius: 12 },
  statusDesc: { fontSize: 13, color: '#888', marginTop: 6, marginBottom: 12 },
  rating: { fontSize: 14, color: '#666', marginBottom: 8 },
  price: { fontSize: 32, fontWeight: 800, color: '#333', marginBottom: 6 },
  perItem: { fontSize: 14, fontWeight: 400, color: '#888' },
  shippingNote: { fontSize: 13, color: '#888', marginBottom: 12 },
  divider: { height: 1, background: '#f0f0f0', margin: '16px 0' },
  sectionLabel: { fontSize: 12, color: '#aaa', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 },
  qtyRow: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 },
  qtyControl: { display: 'flex', alignItems: 'center', border: '1px solid #e8e8e8', borderRadius: 8 },
  qtyBtn: { width: 36, height: 36, background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#555' },
  qtyValue: { width: 44, textAlign: 'center', fontSize: 16, fontWeight: 700 },
  totalRow: { display: 'flex', alignItems: 'center', gap: 16, margin: '12px 0 20px' },
  totalPrice: { fontSize: 28, fontWeight: 800, color: '#b39ddb' },
  joinBtn: { width: '100%', padding: '16px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 700, cursor: 'pointer' },
  desc: { marginBottom: 48 },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: '#333', marginBottom: 16 },
  reviewSection: { borderTop: '1px solid #f0f0f0', paddingTop: 32 },
  reviewHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
  sortToggle: { display: 'flex', gap: 6 },
  sortBtn: { padding: '6px 12px', border: '1px solid #e8e8e8', background: '#fff', borderRadius: 16, fontSize: 12, color: '#666', cursor: 'pointer' },
  sortBtnActive: { background: '#b39ddb', color: '#fff', borderColor: '#b39ddb' },
  noReview: { color: '#bbb', textAlign: 'center', padding: '40px 0' },
  reviewItem: { padding: '16px 0', borderBottom: '1px solid #f5f5f5' },
  reviewMeta: { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 },
  variantChips: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  variantChip: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', border: '2px solid #e8e8e8', borderRadius: 8, background: '#fff', fontSize: 13, color: '#444', cursor: 'pointer' },
  variantChipActive: { borderColor: '#b39ddb', color: '#7e57c2', fontWeight: 700, background: '#f6f2fc' },
  variantChipDisabled: { opacity: 0.45, textDecoration: 'line-through', cursor: 'not-allowed' },
  variantChipImg: { width: 28, height: 28, objectFit: 'cover', borderRadius: 4 },
};

export default GroupBuyDetailPage;
